package br.com.htms.inspeccaocampo.data.colaboradores

import br.com.htms.inspeccaocampo.auth.AuthApi
import br.com.htms.inspeccaocampo.data.JsonMap
import br.com.htms.inspeccaocampo.platform.currentTimeMillis

/** Documento da matriz QSMS (mesma lista de `colaboradoresDados.js`). */
data class DocMatriz(
    val key: String,
    val rotulo: String,
    val descricao: String,
    /** Só marcação (OK), sem data de realização. */
    val marcacao: Boolean = false
)

object DocsMatriz {
    val todos: List<DocMatriz> = listOf(
        DocMatriz("ASO", "ASO", "Atestado de Saúde Ocupacional"),
        DocMatriz("EPI", "Ficha EPI", "Ficha de entrega de EPI — validade 3 meses"),
        DocMatriz("OS", "OS", "Ordem de Serviço assinada — validade 12 meses"),
        DocMatriz("NR06", "NR-06", "Equipamento de Proteção Individual"),
        DocMatriz("NR10", "NR-10", "Segurança em eletricidade"),
        DocMatriz("SEP", "SEP", "NR-10 Sistema Elétrico de Potência"),
        DocMatriz("NR11", "NR-11", "Transporte e movimentação de cargas"),
        DocMatriz("NR12", "NR-12", "Máquinas e equipamentos"),
        DocMatriz("NR18", "NR-18", "Construção civil"),
        DocMatriz("NR20", "NR-20", "Inflamáveis e combustíveis"),
        DocMatriz("NR23", "NR-23", "Proteção contra incêndios"),
        DocMatriz("NR33", "NR-33", "Espaço confinado"),
        DocMatriz("NR35", "NR-35", "Trabalho em altura")
    )

    fun porKey(key: String): DocMatriz? = todos.firstOrNull { it.key == key }

    val cnhCategorias = listOf("", "A", "B", "C", "D", "E", "AB", "AC", "AD", "AE")
}

/** Documento avulso, com nome e validade definidos no cadastro. */
data class DocOutro(
    val id: String = "",
    val nome: String = "",
    /** aaaa-mm-dd */
    val realizado: String = "",
    /** aaaa-mm-dd */
    val validade: String = "",
    val na: Boolean = false
)

data class CnhDados(
    val aplica: String = "",
    val categoria: String = "",
    val numero: String = "",
    /** aaaa-mm-dd */
    val validade: String = "",
    val ear: Boolean = false
)

data class HistColab(val t: Long, val by: String, val acao: String, val campos: List<String>)

data class Colaborador(
    val id: String = "",
    val vinculo: String = "CLT",
    val matricula: String = "",
    val cnpj: String = "",
    val nome: String = "",
    val funcao: String = "",
    val cargoId: String = "",
    val setorGhe: String = "",
    val situacao: String = "",
    /** dd/mm/aaaa */
    val admissao: String = "",
    /** dd/mm/aaaa */
    val nascimento: String = "",
    val rg: String = "",
    val cpf: String = "",
    val ctps: String = "",
    val dirdef: String = "",
    val capacitacao: String = "",
    /** Id do centro de custo (ativos_centros_custo). */
    val ccId: String = "",
    /** key → aaaa-mm-dd, "OK" ou "N/A". */
    val docs: Map<String, String> = emptyMap(),
    val outros: List<DocOutro> = emptyList(),
    val cnh: CnhDados = CnhDados(),
    val obs: String = "",
    val foraMatriz: Boolean = false,
    val totalDocumentos: Int = 0,
    val hist: List<HistColab> = emptyList(),
    val updatedAt: Long = 0L
) {
    /** Entra nos indicadores: situação ATIVO (ou em branco). */
    val elegivel: Boolean get() = situacao.isBlank() || situacao.trim().uppercase().startsWith("ATIVO")
    val desativado: Boolean get() = situacao.trim().uppercase().startsWith("DESATIV")

    /** Ativo na data, igual ao Controle de Efetivo: ATIVO, já admitido. Fora da matriz continua na conta. */
    fun noQuadro(hojeIso: String): Boolean {
        if (!elegivel) return false
        val adm = when {
            admissao.length >= 10 && admissao[4] == '-' -> admissao.take(10)
            else -> DatasColab.entradaParaIso(admissao)
        }
        return adm.isBlank() || adm <= hojeIso
    }
}

data class RegraDoc(val obrig: Boolean, val valCLT: Int?, val valPJ: Int?)

data class ColabRegras(
    val crit: Int = 30,
    val aten: Int = 60,
    val cnhCrit: Int = 30,
    val cnhAten: Int = 60,
    val docs: Map<String, RegraDoc> = PADRAO_DOCS
) {
    fun doc(key: String): RegraDoc = docs[key] ?: PADRAO_DOCS[key] ?: RegraDoc(false, null, null)

    companion object {
        val PADRAO_DOCS: Map<String, RegraDoc> = mapOf(
            "ASO" to RegraDoc(true, 365, 365),
            "EPI" to RegraDoc(true, 90, 90),
            "OS" to RegraDoc(true, 365, 365),
            "NR06" to RegraDoc(true, 730, 730),
            "NR10" to RegraDoc(true, 730, 730),
            "SEP" to RegraDoc(true, 730, 730),
            "NR11" to RegraDoc(false, 730, 730),
            "NR12" to RegraDoc(false, 730, 730),
            "NR18" to RegraDoc(true, 730, 730),
            "NR20" to RegraDoc(false, 1095, 1095),
            "NR23" to RegraDoc(false, 730, 730),
            "NR33" to RegraDoc(false, 365, 365),
            "NR35" to RegraDoc(false, 730, 730)
        )
    }
}

enum class StatusDoc(val rotulo: String, val cor: Long) {
    VALIDO("Válido", 0xFF1B7F3B),
    ATENCAO("Atenção", 0xFFE0A000),
    CRITICO("Crítico", 0xFFE07B00),
    VENCIDO("Vencido", 0xFFC62828),
    PENDENTE("Pendente", 0xFF7B1FA2),
    SEM_REGISTRO("Sem registro", 0xFF8A94A6),
    NA("N/A", 0xFF8A94A6),
    NAO_INFORMADO("Não informado", 0xFF8A94A6),
    VERIFICAR("Verificar", 0xFFE0A000);

    /** Em dia para a conformidade (válido, atenção ou crítico). */
    val emDia: Boolean get() = this == VALIDO || this == ATENCAO || this == CRITICO
}

/** Resultado do cálculo de um documento: vencimento em aaaa-mm-dd e dias até ele. */
data class SituacaoDoc(
    val status: StatusDoc,
    val realizado: String = "",
    val vencimento: String = "",
    val dias: Int? = null,
    val texto: String = ""
)

object DatasColab {
    private const val DIA_MS = 86_400_000L
    private const val FUSO_BR_MS = 3 * 3_600_000L

    /** Dias desde 1970-01-01 no horário de Brasília. */
    fun hoje(): Long = (currentTimeMillis() - FUSO_BR_MS).floorDiv(DIA_MS)

    fun diasDeIso(iso: String): Long? {
        val m = Regex("^(\\d{4})-(\\d{2})-(\\d{2})").find(iso.trim()) ?: return null
        val (y, mo, d) = m.destructured
        return diasCivis(y.toInt(), mo.toInt(), d.toInt())
    }

    fun diasDeBr(br: String): Long? = diasDeIso(brParaIso(br))

    fun isoDeDias(dias: Long): String {
        val z = dias + 719468
        val era = (if (z >= 0) z else z - 146096) / 146097
        val doe = z - era * 146097
        val yoe = (doe - doe / 1460 + doe / 36524 - doe / 146096) / 365
        val doy = doe - (365 * yoe + yoe / 4 - yoe / 100)
        val mp = (5 * doy + 2) / 153
        val d = doy - (153 * mp + 2) / 5 + 1
        val m = if (mp < 10) mp + 3 else mp - 9
        val y = yoe + era * 400 + if (m <= 2) 1 else 0
        return "${y.toString().padStart(4, '0')}-${m.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}"
    }

    private fun diasCivis(y0: Int, m: Int, d: Int): Long? {
        if (m !in 1..12 || d !in 1..31 || y0 < 1900 || y0 > 2100) return null
        val y = (if (m <= 2) y0 - 1 else y0).toLong()
        val era = (if (y >= 0) y else y - 399) / 400
        val yoe = y - era * 400
        val doy = (153 * (if (m > 2) m - 3 else m + 9) + 2) / 5 + d - 1
        val doe = yoe * 365 + yoe / 4 - yoe / 100 + doy
        return era * 146097 + doe - 719468
    }

    fun isoParaBr(iso: String): String {
        val m = Regex("^(\\d{4})-(\\d{2})-(\\d{2})").find(iso.trim()) ?: return ""
        val (y, mo, d) = m.destructured
        return "$d/$mo/$y"
    }

    fun brParaIso(br: String): String {
        val m = Regex("^(\\d{2})/(\\d{2})/(\\d{4})$").find(br.trim()) ?: return ""
        val (d, mo, y) = m.destructured
        return "$y-$mo-$d"
    }

    /** Aceita dd/mm/aaaa (com ou sem barras) e devolve aaaa-mm-dd, ou "" se inválida. */
    fun entradaParaIso(texto: String): String {
        val t = texto.trim()
        if (t.isEmpty()) return ""
        val d = t.filter { it.isDigit() }
        val br = when {
            Regex("^\\d{2}/\\d{2}/\\d{4}$").matches(t) -> t
            d.length == 8 -> "${d.substring(0, 2)}/${d.substring(2, 4)}/${d.substring(4)}"
            else -> return ""
        }
        val iso = brParaIso(br)
        return if (diasDeIso(iso) != null && isoDeDias(diasDeIso(iso)!!) == iso) iso else ""
    }

    fun idade(nascimentoBr: String): Int? {
        val n = diasDeBr(nascimentoBr) ?: return null
        return ((hoje() - n) / 365.2425).toInt()
    }
}

object StatusColab {
    private val CONDUCAO = Regex("MOTORISTA|OPERADOR|MUNCK|GUINDAUTO|CONDUTOR|CAMINH|MAQUINAS|MÁQUINAS")

    fun doc(c: Colaborador, key: String, regras: ColabRegras, hoje: Long = DatasColab.hoje()): SituacaoDoc {
        val regra = regras.doc(key)
        val v = c.docs[key].orEmpty()
        if (v == "N/A") return SituacaoDoc(StatusDoc.NA)
        val semRegistro = SituacaoDoc(if (regra.obrig) StatusDoc.PENDENTE else StatusDoc.SEM_REGISTRO)
        if (DocsMatriz.porKey(key)?.marcacao == true) {
            return if (v == "OK") SituacaoDoc(StatusDoc.VALIDO, texto = "OK") else semRegistro
        }
        val real = DatasColab.diasDeIso(v) ?: return semRegistro
        val validade = if (c.vinculo == "PJ") regra.valPJ else regra.valCLT
        if (validade == null || validade <= 0) return SituacaoDoc(StatusDoc.VALIDO, realizado = v)
        val venc = real + validade
        val dias = (venc - hoje).toInt()
        val st = when {
            dias < 0 -> StatusDoc.VENCIDO
            dias <= regras.crit -> StatusDoc.CRITICO
            dias <= regras.aten -> StatusDoc.ATENCAO
            else -> StatusDoc.VALIDO
        }
        return SituacaoDoc(st, realizado = v, vencimento = DatasColab.isoDeDias(venc), dias = dias)
    }

    /** Validade informada no cadastro, não a regra fixa da matriz. */
    fun outro(d: DocOutro, regras: ColabRegras, hoje: Long = DatasColab.hoje()): SituacaoDoc {
        if (d.na) return SituacaoDoc(StatusDoc.NA)
        val v = DatasColab.diasDeIso(d.validade)
            ?: return SituacaoDoc(StatusDoc.PENDENTE, realizado = d.realizado, texto = "Validade não informada")
        val dias = (v - hoje).toInt()
        val st = when {
            dias < 0 -> StatusDoc.VENCIDO
            dias <= regras.crit -> StatusDoc.CRITICO
            dias <= regras.aten -> StatusDoc.ATENCAO
            else -> StatusDoc.VALIDO
        }
        return SituacaoDoc(st, realizado = d.realizado, vencimento = d.validade, dias = dias)
    }

    fun funcaoDeConducao(funcao: String): Boolean = CONDUCAO.containsMatchIn(funcao.uppercase())

    fun cnh(c: Colaborador, regras: ColabRegras, hoje: Long = DatasColab.hoje()): SituacaoDoc {
        val cnh = c.cnh
        if (cnh.aplica == "sim") {
            val v = DatasColab.diasDeIso(cnh.validade)
                ?: return SituacaoDoc(StatusDoc.PENDENTE, texto = "Validade não informada")
            val dias = (v - hoje).toInt()
            val st = when {
                dias < 0 -> StatusDoc.VENCIDO
                dias <= regras.cnhCrit -> StatusDoc.CRITICO
                dias <= regras.cnhAten -> StatusDoc.ATENCAO
                else -> StatusDoc.VALIDO
            }
            return SituacaoDoc(st, vencimento = cnh.validade, dias = dias)
        }
        if (cnh.aplica == "nao") return SituacaoDoc(StatusDoc.NA, texto = "Não se aplica")
        if (funcaoDeConducao(c.funcao)) return SituacaoDoc(StatusDoc.VERIFICAR, texto = "Verificar (função de condução)")
        return SituacaoDoc(StatusDoc.NAO_INFORMADO, texto = "Não informado")
    }

    private val PIOR = listOf(
        StatusDoc.VENCIDO, StatusDoc.PENDENTE, StatusDoc.CRITICO, StatusDoc.ATENCAO,
        StatusDoc.VALIDO, StatusDoc.SEM_REGISTRO, StatusDoc.NA
    )

    /** Pior situação entre os documentos (para ordenar e colorir a linha). */
    fun pior(c: Colaborador, regras: ColabRegras, hoje: Long = DatasColab.hoje()): StatusDoc =
        (DocsMatriz.todos.map { doc(c, it.key, regras, hoje).status } + c.outros.map { outro(it, regras, hoje).status })
            .minByOrNull { PIOR.indexOf(it).let { i -> if (i < 0) PIOR.size else i } } ?: StatusDoc.NA

    fun temPendencia(c: Colaborador, regras: ColabRegras, hoje: Long = DatasColab.hoje()): Boolean =
        DocsMatriz.todos.any {
            val s = doc(c, it.key, regras, hoje).status
            s == StatusDoc.PENDENTE || s == StatusDoc.VENCIDO
        } || c.outros.any {
            val s = outro(it, regras, hoje).status
            s == StatusDoc.PENDENTE || s == StatusDoc.VENCIDO
        }
}

data class CentroColab(
    val id: String,
    val codigo: String,
    val nome: String,
    val ativo: Boolean
) {
    val rotulo: String get() = listOf(codigo, nome).filter { it.isNotBlank() }.joinToString(" · ")
}

data class ColabDados(
    val colaboradores: List<Colaborador>,
    val regras: ColabRegras,
    val master: Boolean,
    val podeRegras: Boolean = master,
    val podeEditar: Boolean = true,
    val podeExcluir: Boolean = false,
    val centros: List<CentroColab> = emptyList()
)

data class ResumoImportacao(
    val atualizados: Int,
    val inseridos: Int,
    val foraDaMatriz: Int,
    val repetidos: Int,
    val rejeitados: List<String>
)

/** Cliente das rotas `/colaboradores` do Worker (`colaboradoresApi.js`). */
object ColaboradoresApi {
    suspend fun dados(): Result<ColabDados> =
        AuthApi.apiJson("GET", "/colaboradores").mapCatching { text ->
            val o = JsonMap.parse(text)
            ColabDados(
                colaboradores = o.array("colaboradores").orEmpty().mapNotNull { (it as? JsonMap)?.let(::colaboradorDe) },
                regras = o.obj("regras")?.let(::regrasDe) ?: ColabRegras(),
                master = o.bool("master"),
                podeRegras = o.bool("master") || o.bool("podeRegras"),
                podeEditar = o.bool("podeEditar", true),
                podeExcluir = o.bool("podeExcluir"),
                centros = o.array("centros").orEmpty().mapNotNull { (it as? JsonMap)?.let(::centroDe) }
            )
        }

    suspend fun salvar(c: Colaborador, acao: String = ""): Result<Colaborador> {
        val body = colaboradorJson(c, acao)
        val r = if (c.id.isBlank()) {
            AuthApi.apiJson("POST", "/colaboradores", body)
        } else {
            AuthApi.apiJson("PUT", "/colaboradores/${enc(c.id)}", body)
        }
        return r.mapCatching { text ->
            JsonMap.parse(text).obj("colaborador")?.let(::colaboradorDe) ?: error("Resposta inválida do servidor")
        }
    }

    suspend fun excluir(id: String): Result<Unit> =
        AuthApi.apiJson("DELETE", "/colaboradores/${enc(id)}", "{}").map { }

    suspend fun salvarRegras(r: ColabRegras): Result<ColabRegras> =
        AuthApi.apiJson("PUT", "/colaboradores/regras", regrasJson(r)).mapCatching { text ->
            JsonMap.parse(text).obj("regras")?.let(::regrasDe) ?: r
        }

    /** [json] no formato do backup do portal antigo: { regras?, colaboradores: [...] }. */
    suspend fun importar(json: String): Result<ResumoImportacao> =
        AuthApi.apiJson("POST", "/colaboradores/importar", json).mapCatching { text ->
            val o = JsonMap.parse(text)
            ResumoImportacao(
                atualizados = (o.long("atualizados") ?: 0L).toInt(),
                inseridos = (o.long("inseridos") ?: 0L).toInt(),
                foraDaMatriz = (o.long("foraDaMatriz") ?: 0L).toInt(),
                repetidos = (o.long("repetidos") ?: 0L).toInt(),
                rejeitados = o.array("rejeitados").orEmpty().mapNotNull { it as? String }
            )
        }

    /** Backup no mesmo formato do portal antigo (pode ser reimportado). */
    fun backupJson(dados: ColabDados, geradoEm: String): String = buildString {
        append("{\"formato\":\"portal-qsms-backup\",\"versao\":1,\"empresa\":\"HTMS Energia\",")
        append("\"geradoEm\":").append(jsonStr(geradoEm)).append(',')
        append("\"regras\":").append(regrasJson(dados.regras)).append(',')
        append("\"colaboradores\":[")
        dados.colaboradores.forEachIndexed { i, c ->
            if (i > 0) append(',')
            append(colaboradorJson(c, ""))
        }
        append("]}")
    }

    private fun centroDe(o: JsonMap): CentroColab? {
        val id = o.str("id").ifBlank { return null }
        return CentroColab(id, o.str("codigo"), o.str("nome"), o.bool("ativo", true))
    }

    private fun colaboradorDe(o: JsonMap): Colaborador? {
        val id = o.str("id").ifBlank { return null }
        val docs = o.obj("docs")?.let { d ->
            DocsMatriz.todos.mapNotNull { k -> d.str(k.key).takeIf { it.isNotBlank() }?.let { k.key to it } }.toMap()
        }.orEmpty()
        val cnh = o.obj("cnh")?.let {
            CnhDados(
                aplica = it.str("aplica"),
                categoria = it.str("categoria"),
                numero = it.str("numero"),
                validade = it.str("validade"),
                ear = it.bool("ear")
            )
        } ?: CnhDados()
        val hist = o.array("hist").orEmpty().mapNotNull { h ->
            (h as? JsonMap)?.let {
                HistColab(
                    t = it.long("t") ?: 0L,
                    by = it.str("by"),
                    acao = it.str("acao"),
                    campos = it.array("campos").orEmpty().mapNotNull { x -> x as? String }
                )
            }
        }
        return Colaborador(
            id = id,
            vinculo = o.str("vinculo").ifBlank { "CLT" },
            matricula = o.str("matricula"),
            cnpj = o.str("cnpj"),
            nome = o.str("nome"),
            funcao = o.str("funcao"),
            cargoId = o.str("cargoId"),
            setorGhe = o.str("setorGhe"),
            situacao = o.str("situacao"),
            admissao = o.str("admissao"),
            nascimento = o.str("nascimento"),
            rg = o.str("rg"),
            cpf = o.str("cpf"),
            ctps = o.str("ctps"),
            dirdef = o.str("dirdef"),
            capacitacao = o.str("capacitacao"),
            ccId = o.str("ccId"),
            docs = docs,
            outros = o.array("outros").orEmpty().mapNotNull { item ->
                val m = item as? JsonMap ?: return@mapNotNull null
                val nome = m.str("nome").trim()
                if (nome.isBlank()) return@mapNotNull null
                DocOutro(m.str("id"), nome, m.str("realizado"), m.str("validade"), m.bool("na"))
            },
            cnh = cnh,
            obs = o.str("obs"),
            foraMatriz = o.bool("foraMatriz"),
            totalDocumentos = (o.long("totalDocumentos") ?: 0L).toInt(),
            hist = hist,
            updatedAt = o.long("updatedAt") ?: 0L
        )
    }

    private fun regrasDe(o: JsonMap): ColabRegras {
        val d = o.obj("docs")
        val docs = DocsMatriz.todos.associate { k ->
            val padrao = ColabRegras.PADRAO_DOCS.getValue(k.key)
            val r = d?.obj(k.key)
            k.key to if (r == null) padrao else RegraDoc(
                obrig = r.bool("obrig", padrao.obrig),
                valCLT = r.long("valCLT")?.toInt(),
                valPJ = r.long("valPJ")?.toInt()
            )
        }
        return ColabRegras(
            crit = (o.long("crit") ?: 30L).toInt(),
            aten = (o.long("aten") ?: 60L).toInt(),
            cnhCrit = (o.long("cnhCrit") ?: 30L).toInt(),
            cnhAten = (o.long("cnhAten") ?: 60L).toInt(),
            docs = docs
        )
    }

    private fun colaboradorJson(c: Colaborador, acao: String): String = buildString {
        append('{')
        fun campo(k: String, v: String) {
            append(jsonStr(k)).append(':').append(jsonStr(v)).append(',')
        }
        campo("vinculo", c.vinculo)
        campo("matricula", c.matricula)
        campo("cnpj", c.cnpj)
        campo("nome", c.nome)
        campo("funcao", c.funcao)
        campo("situacao", c.situacao)
        campo("admissao", c.admissao)
        campo("nascimento", c.nascimento)
        campo("rg", c.rg)
        campo("cpf", c.cpf)
        campo("ctps", c.ctps)
        campo("dirdef", c.dirdef)
        campo("capacitacao", c.capacitacao)
        campo("ccId", c.ccId)
        campo("obs", c.obs)
        if (acao.isNotBlank()) campo("acao", acao)
        append("\"docs\":{")
        append(c.docs.entries.joinToString(",") { (k, v) -> jsonStr(k) + ":" + jsonStr(v) })
        append("},\"cnh\":{")
        append("\"aplica\":").append(jsonStr(c.cnh.aplica)).append(',')
        append("\"categoria\":").append(jsonStr(c.cnh.categoria)).append(',')
        append("\"numero\":").append(jsonStr(c.cnh.numero)).append(',')
        append("\"validade\":").append(jsonStr(c.cnh.validade)).append(',')
        append("\"ear\":").append(if (c.cnh.ear) "true" else "false")
        append("},\"outros\":[")
        c.outros.forEachIndexed { i, d ->
            if (i > 0) append(',')
            append("{\"id\":").append(jsonStr(d.id))
            append(",\"nome\":").append(jsonStr(d.nome))
            append(",\"realizado\":").append(jsonStr(d.realizado))
            append(",\"validade\":").append(jsonStr(d.validade))
            append(",\"na\":").append(if (d.na) "true" else "false").append('}')
        }
        append("]}")
    }

    private fun regrasJson(r: ColabRegras): String = buildString {
        append("{\"crit\":${r.crit},\"aten\":${r.aten},\"cnhCrit\":${r.cnhCrit},\"cnhAten\":${r.cnhAten},\"docs\":{")
        append(
            DocsMatriz.todos.joinToString(",") { k ->
                val d = r.doc(k.key)
                "\"${k.key}\":{\"obrig\":${d.obrig},\"valCLT\":${d.valCLT ?: "null"},\"valPJ\":${d.valPJ ?: "null"}}"
            }
        )
        append("}}")
    }

    private fun jsonStr(s: String): String = buildString {
        append('"')
        for (c in s) {
            when (c) {
                '\\' -> append("\\\\")
                '"' -> append("\\\"")
                '\n' -> append("\\n")
                '\r' -> append("\\r")
                '\t' -> append("\\t")
                else -> if (c.code < 0x20) append("\\u" + c.code.toString(16).padStart(4, '0')) else append(c)
            }
        }
        append('"')
    }

    private fun enc(s: String): String = buildString {
        for (b in s.encodeToByteArray()) {
            val c = b.toInt().toChar()
            if (c.isLetterOrDigit() && b >= 0 || c == '-' || c == '_' || c == '.' || c == '~') append(c)
            else append('%').append(((b.toInt() and 0xFF).toString(16).uppercase()).padStart(2, '0'))
        }
    }
}